import * as Yup from "yup";

import { extractEmails, getCookie, planTypeMap, showAlert } from "utils/utils";
import { useHistory, useLocation } from "react-router-dom";
import { registerUser, trackEvents } from "api";

import AuthLayout, { FormHeader } from "components/auth/AuthLayout";
import Button from "components/global/Button";
import FormGroup from "components/global/FormGroup";
import FormWrapper from "components/global/FormWrapper";
import { Formik } from "formik";
import { Link } from "react-router-dom";
import Spacer from "components/global/Spacer";
import Logo from "components/global/Logo";
import usePageTitle from "hooks/usePageTitle";
import styled from "styled-components";
import DoneIcon from "assets/icons/done_icon.svg";

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) =>
    // @ts-ignore
    props.isDone ? "green" : "#9A9AAF"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  margin-right: 5px;
`;

const DotRender = ({ isDone }) => {
  return (
    <>
      {isDone ? (
        <img src={DoneIcon} alt="done" style={{ marginRight: "5px" }} />
      ) : (
        <Dot />
      )}
    </>
  );
};

const RegisterWithEmail = (props) => {
  usePageTitle("Sign up");
  const router = useHistory();
  const { search } = useLocation();
  const redirectPath = new URLSearchParams(search).get("return_to");
  const full_name = new URLSearchParams(search).get("full_name");
  const email = extractEmails(search)?.[0];
  const invite = new URLSearchParams(search).get("invite");
  const planType = new URLSearchParams(search).get("plan");
  const plan = planType ? planTypeMap[planType.toLowerCase()] : null;
  const postpaddyMobile = new URLSearchParams(search).get("postpaddy-mobile");
  const referralCode = new URLSearchParams(search).get("referralCode");

  const utm_source = getCookie("utm_source");
  const utm_medium = getCookie("utm_medium");
  const utm_campaign = getCookie("utm_campaign");
  const utm_content = getCookie("utm_content");

  // track signup event
  const trackSignup = async () => {
    const date = new Date();
    const data = {
      event_name: "clicked_signup",
      event_properties: {
        date: date,
        account_type: planType,
      },
    };
    if (utm_campaign || utm_content || utm_medium || utm_source) {
      data.utm_tags = {
        source: utm_source,
        medium: utm_medium,
        campaign: utm_campaign,
        content: utm_content,
      };
    }

    try {
      await trackEvents(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleRegister = async (cred) => {
    const data = { ...cred };

    if (invite) {
      data.invite = invite;
    }

    if (plan) {
      data.plan_id = plan;
    }

    if (!data.referral_code) {
      delete data.referral_code;
    }

    try {
      const res = await registerUser(data);

      if (res?.data?.status === true) {
        trackSignup();

        router.push(
          `/onboarding${redirectPath ? `?return_to=${redirectPath}` : ""}`
        );

        // @ts-ignore
        window.gr?.("track", "conversion", { email: data.email }); // GTM tracker

        // Snapchat tracker
        // @ts-ignore
        window.snaptr?.("init", "10aed44c-c452-49bf-a3e3-8330bc2edb3e", {
          user_email: data.email,
        });
        // @ts-ignore
        window.snaptr?.("track", "SIGN_UP");
      } else if (res?.data?.status === false || res?.data?.errors) {
        showAlert({
          msg: res?.data?.message ?? "Incorrect email or password",
          type: "danger",
        });
      }
    } catch (e) {
      showAlert({
        msg: e.response?.data?.message ?? "An error occurred",
        type: "danger",
      });
    }
  };

  const schema = Yup.object({
    full_name: Yup.string().required("Field required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Field required"),
    phone: Yup.string(),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .matches(/[0-9]/, "Password must contain at least one digit")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .required("Field required"),
    referral_code: Yup.string(),
  });

  return (
    <AuthLayout>
      <FormHeader>
        <Logo />
        <p className="l3">
          Already have an account?{" "}
          <Link
            to={
              redirectPath
                ? `/sign-in${search}`
                : postpaddyMobile
                ? "/sign-in?postpaddy-mobile=true"
                : "/sign-in"
            }
            className="signInText l3"
          >
            Sign in
          </Link>
        </p>
      </FormHeader>
      <Spacer y={3.2} />
      <h4 className="title">Sign up to PostPaddy</h4>
      <Spacer y={0.6} />
      <p className="l3">Let's get your account set up.</p>
      <Spacer y={2.4} />

      <Formik
        initialValues={{
          full_name: full_name || "",
          email: email ? decodeURIComponent(email) : "",
          phone: "",
          password: "",
          referral_code: referralCode ?? "",
        }}
        validationSchema={schema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          await handleRegister(values);
          setSubmitting(false);
        }}
      >
        {({ handleSubmit, isSubmitting, isValid, values, errors }) => (
          <FormWrapper onSubmit={handleSubmit}>
            <FormGroup
              fieldStyle="shortText"
              label="Full name"
              name="full_name"
              placeholder="e.g John"
              className="spanFull"
              // required={false}
            />
            <FormGroup
              fieldStyle="shortText"
              type="email"
              label="Email address"
              name="email"
              placeholder="e.g johndoe@email.com"
              className="spanFull"
              // required={false}
            />

            <FormGroup
              fieldStyle="shortText"
              type="tel"
              label="Phone number"
              name="phone"
              required={false}
              phoneInput
              className="spanFull"
            />

            <FormGroup
              fieldStyle="shortText"
              type="password"
              label="Password"
              name="password"
              className="password spanFull"
              placeholder="Your password"
              // required={false}
            />

            <div className="l4 validators spanFull">
              <p
                className={`flexRow alignCenter ${
                  values.password.length >= 8 ? "colorSuccess" : "invalid"
                }`}
              >
                <DotRender isDone={values.password.length >= 8} />
                At least 8 characters long
              </p>
              <p
                className={`flexRow alignCenter ${
                  values.password.match(/[A-Z]/g) ? "colorSuccess" : "invalid"
                }`}
              >
                <DotRender isDone={values.password.match(/[A-Z]/g)} />
                Contains uppercase character
              </p>
              <p
                className={`flexRow alignCenter ${
                  values.password.match(/[A-Z]/g) ? "colorSuccess" : "invalid"
                }`}
              >
                <DotRender isDone={values.password.match(/[a-z]/g)} />
                Contains lowercase character
              </p>
              <p
                className={`flexRow alignCenter ${
                  values.password.match(/[0-9]/g) ? "colorSuccess" : "invalid"
                }`}
              >
                <DotRender isDone={values.password.match(/[0-9]/g)} />
                Contains 1 number
              </p>
            </div>

            <FormGroup
              fieldStyle="shortText"
              label="Referral code"
              name="referral_code"
              className="spanFull"
              required={false}
            />

            <div className="spanFull">
              <Button
                type="submit"
                text="Sign up"
                disabled={isSubmitting || !isValid}
                loading={isSubmitting}
                fullWidth
              />
              <Spacer y={2.4} yMobile={1.4} />
              <p className="l4 colorBodyText legal textCenter">
                By continuing with Google or Email, you agree to PostPaddy's{" "}
                <a
                  href="https://postpaddy.com/pages/terms-of-service "
                  className="colorSecondary l4 textNoWrap"
                >
                  Terms of service
                </a>{" "}
                and{" "}
                <a
                  href="https://postpaddy.com/pages/privacy-policy"
                  className="colorSecondary l4 textNoWrap"
                >
                  Privacy policy
                </a>
                .
              </p>
            </div>
          </FormWrapper>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default RegisterWithEmail;
